import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import type { ApExceptionRuleSet } from '@erp/rpc';
import { useApExceptionRuleSet } from 'src/api/rpc/hooks/accounts-payable/useApExceptionRuleSets';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';

/**
 * Loads one rule set and renders children, or the shared loading / error /
 * missing states used by the view and edit pages.
 */
export function ExceptionRuleSetLoadGate({
  ruleSetId,
  children,
}: {
  ruleSetId: string;
  children: (ruleSet: ApExceptionRuleSet) => ReactNode;
}) {
  const { data, isLoading, isError, refetch } =
    useApExceptionRuleSet(ruleSetId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-sm text-muted-foreground">
          <T context="exception rules: loading one rule set">
            Loading rule set…
          </T>
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-3 p-1">
        <Button variant="ghost" size="sm" className="self-start" asChild>
          <Link to="/accounts-payable/exception-rules">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            <T context="exception rules: back to list from a failed load">
              Back to Exception Rules
            </T>
          </Link>
        </Button>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-destructive">
            <T context="exception rules: failed to load one rule set">
              Could not load this rule set.
            </T>
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            <T context="exception rules: retry loading one rule set">Retry</T>
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-3 p-1">
        <Button variant="ghost" size="sm" className="self-start" asChild>
          <Link to="/accounts-payable/exception-rules">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            <T context="exception rules: back to list from a missing set">
              Back to Exception Rules
            </T>
          </Link>
        </Button>
        <p className="text-sm text-muted-foreground">
          <T context="exception rules: rule set not found">
            This rule set was not found. It may have been deleted.
          </T>
        </p>
      </div>
    );
  }

  return children(data);
}
